import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { MenuItem } from '../../../../../core/api/menu/menu.types';
import {
  AddItemHookPayload,
  BasketSource,
  RemoveItemHookPayload,
} from '../../../../../core/types/basket.types';
import { ClickItemPayloadType } from '../../../../../core/types/menu.types';
import { Card } from '../../../../atoms/Card';
import { Image } from '../../../../atoms/Image/Image';
import { PriceTag } from '../../../../atoms/PriceTag';
import { QuantityControl } from '../../../../atoms/QuantityControl';
import { Typography } from '../../../../atoms/Typography';

export type Props = {
  localisedPrice: string;
  menuItem: MenuItem;
  onAddItem: (payload: AddItemHookPayload) => void;
  onClickItem?: (payload: ClickItemPayloadType) => void;
  onRemoveItem?: (payload: RemoveItemHookPayload) => void;
  quantity: number;
  source: BasketSource;
  translate: TranslateFunction;
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexGrow: 1,
    padding: theme.spacing(1, 1.5),

    '&.image': {
      paddingLeft: theme.spacing(1),
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: '80px', // to match the height of the image
    width: '100%',

    '& .menu-item-content': {
      '&--name, &--description': {
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': 1,
        display: '-webkit-box',
        overflow: 'hidden',
        wordBreak: 'break-word',
      },
      '&--name': {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
  },
  image: {
    boxSizing: 'content-box',
    minWidth: '80px',
    objectFit: 'cover',
    paddingRight: theme.spacing(1),
  },
  controls: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
}));

export function MenuListItem(props: Props): JSX.Element {
  const {
    localisedPrice,
    menuItem,
    onAddItem,
    onClickItem,
    onRemoveItem,
    quantity,
    source,
    translate,
  } = props;
  const classes = useStyles();

  const hasImage = !!menuItem.ImageUrl;
  const hasOptions = menuItem.MenuItemOptionSets.length > 0;

  const handleOnClick = (): void => {
    onClickItem({ menuId: menuItem.MenuId, item: menuItem });
  };

  const handleOnAddItem = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ): void => {
    if (onClickItem) {
      event.stopPropagation();
    }

    onAddItem({ item: menuItem, source });
  };

  const handleOnRemoveItem = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ): void => {
    if (onClickItem) {
      event.stopPropagation();
    }

    onRemoveItem({ item: menuItem, source });
  };

  const containerStyles = clsx(classes.container, hasImage && 'image');

  return (
    <Card
      className="fd-menu-items-list__menu-item"
      data-fd={`list-menu-item-${menuItem.Name}`}
      onClick={onClickItem ? handleOnClick : undefined}
      role="listitem"
    >
      <div className={containerStyles}>
        {menuItem.ImageUrl && (
          <Image
            alt={menuItem.Name}
            className={classes.image}
            dimensions={[80, 80]}
            src={menuItem.ImageUrl}
          />
        )}

        <div className={classes.content}>
          <div>
            <Typography className="menu-item-content--name" variant="body2">
              {menuItem.Name}
            </Typography>
            <Typography
              className="menu-item-content--description"
              variant="body2-secondary"
            >
              {menuItem.Description}
            </Typography>
          </div>

          <div className={classes.controls}>
            <PriceTag
              hasMenuOptions={hasOptions}
              horizontalAlign="right"
              localisedPrice={localisedPrice}
              translate={translate}
              variant="small"
              verticalAlign="bottom"
            />

            <QuantityControl
              itemName={menuItem.Name}
              onAddItem={handleOnAddItem}
              onRemoveItem={onRemoveItem ? handleOnRemoveItem : undefined}
              quantity={onRemoveItem ? quantity : undefined}
              translate={translate}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
